import React from "react";
import { LegalLayout, Section } from "@/components/LegalLayout";

export default function Terms() {
  return (
    <LegalLayout title="Terms of Service" updated="June 2026">
      <p>
        These Terms of Service ("Terms") govern your access to and use of the InfluencerCollab marketplace at
        influencercollab.net (the "Platform"). By creating an account or using the Platform, you agree to these Terms.
        If you do not agree, please do not use the Platform.
      </p>

      <Section heading="1. The Platform">
        <p>
          InfluencerCollab provides technology that connects promoters (brands and businesses) with influencers
          (creators) to post campaigns, submit bids, select creators, deliver content and manage campaign payments.
          We provide discovery, communication, content-delivery and transaction infrastructure — we are not the
          advertiser and do not own the influencer relationship.
        </p>
      </Section>

      <Section heading="2. Accounts & Eligibility">
        <p>
          You must sign in with a valid Google account and provide accurate profile information. You are responsible
          for activity under your account and for keeping your details current. You must be legally able to enter
          into contracts in your jurisdiction.
        </p>
      </Section>

      <Section heading="3. Campaigns, Bids & Selection">
        <ul className="list-disc pl-6 space-y-1.5">
          <li>Promoters are responsible for the accuracy of campaign briefs, budgets and requirements.</li>
          <li>Influencers are responsible for the accuracy of their bids, deliverables and claimed metrics.</li>
          <li>Selecting a bid forms an agreement between the promoter and the influencer on the stated terms.</li>
        </ul>
      </Section>

      <Section heading="4. Platform Fee">
        <p>
          InfluencerCollab charges a <strong>20% platform processing fee</strong> on successful transactions. Applicable
          taxes, payment-gateway charges and other transaction-related costs, where relevant, will be displayed before
          payment. Fees already earned are non-refundable except where required by law.
        </p>
      </Section>

      <Section heading="5. Payments, Approval & Release">
        <p>
          Where payment workflows are used, campaign funds may be held by the Platform until the agreed content is
          delivered and approved, subject to the Platform's applicable terms and the rules of the relevant
          payment-service provider. Approval periods, refunds, disputes and release conditions are governed by those
          terms and applicable law.
        </p>
      </Section>

      <Section heading="6. Content & Intellectual Property">
        <p>
          Influencers retain ownership of content they create except as transferred through agreed usage rights.
          Promoters and influencers are responsible for content accuracy, claims, advertising disclosures, and
          compliance with intellectual-property rights and the rules of relevant social-media platforms. Protected or
          watermarked previews must not be used before payment approval.
        </p>
      </Section>

      <Section heading="7. Prohibited Conduct">
        <ul className="list-disc pl-6 space-y-1.5">
          <li>Misrepresenting identity, metrics, audience or deliverables.</li>
          <li>Circumventing the Platform to avoid fees after being introduced through it.</li>
          <li>Posting unlawful, infringing, misleading or harmful content.</li>
          <li>Attempting to disrupt, reverse-engineer or abuse the Platform.</li>
        </ul>
      </Section>

      <Section heading="8. Disputes">
        <p>
          If something goes wrong, either party may raise a dispute through the Platform. We may review campaign
          records, submitted files and agreed terms before applying the applicable dispute policy. The Platform does
          not guarantee influencer performance, reach, engagement, sales or campaign results.
        </p>
      </Section>

      <Section heading="9. Disclaimers & Limitation of Liability">
        <p>
          The Platform is provided "as is" without warranties of any kind. To the maximum extent permitted by law,
          InfluencerCollab is not liable for indirect, incidental or consequential damages, or for the conduct,
          content or performance of any user. Our total liability for any claim is limited to the fees we received
          for the relevant transaction.
        </p>
      </Section>

      <Section heading="10. Termination">
        <p>
          You may stop using the Platform at any time. We may suspend or terminate accounts that breach these Terms
          or applicable law. Certain provisions survive termination, including fee obligations, IP terms and liability
          limitations.
        </p>
      </Section>

      <Section heading="11. Changes & Contact">
        <p>
          We may update these Terms from time to time; the "Last updated" date reflects the latest version. Continued
          use constitutes acceptance. Questions? Contact{" "}
          <a href="mailto:support@influencercollab.net" className="text-brand-pink hover:opacity-70">support@influencercollab.net</a>.
        </p>
      </Section>
    </LegalLayout>
  );
}
